import React, { useState, useEffect } from 'react';
import { useProgressChecker } from '../hooks/useProgressChecker';
import { useApp } from "../context/AppContext";
import { isGeminiConfigured, testGeminiConnection } from '../services/geminiService';
import Toast from './Toast';


const MissionPanel = ({ challenge, progress, onHint, onValidate }) => {
  const { checkProgress, isChecking, generateAIHint, isGeneratingHint, aiHint } = useProgressChecker();
  const { completeChallenge, completedChallenges, user } = useApp();
  const [localProgress, setLocalProgress] = useState(progress);
  const [showCelebration, setShowCelebration] = useState(false);
  const [simpleMode, setSimpleMode] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [localAiHint, setLocalAiHint] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [testingConnection, setTestingConnection] = useState(false);

  if (!challenge) return null;

  const currentProgress = localProgress || progress;
  const progressPercentage = (currentProgress.completed / challenge.objectives.length) * 100;

  // Update local AI hint when it changes from useProgressChecker
  useEffect(() => {
    if (aiHint && !aiLoading) {
      setLocalAiHint(aiHint);
      setAiError(null);
    }
  }, [aiHint, aiLoading]);

  // Show celebration modal when challenge becomes complete
  useEffect(() => {
    if (currentProgress.isComplete && !showCelebration) {
      setShowCelebration(true);
      setToastMessage(`🎉 Challenge complete! You've completed ${completedChallenges.length} total.`);
      setShowToast(true);
    }
  }, [currentProgress.isComplete, showCelebration, completedChallenges.length]);

  const handleValidate = async () => {
    const newProgress = await checkProgress(challenge);
    setLocalProgress(newProgress);
    if (newProgress.isComplete) {
      completeChallenge(challenge.id); // 🔥 THIS UPDATES UI
    }
    if (onValidate) {
      onValidate(newProgress);
    }
  };

  const closeCelebration = () => {
    setShowCelebration(false);
  };

  const handleAIHint = async () => {
    // Safety checks
    if (!challenge) {
      setAiError('No challenge selected.');
      return;
    }

    setAiLoading(true);
    setAiError(null);
    setLocalAiHint(null);

    try {
      await generateAIHint(challenge, currentProgress, simpleMode);
      // The useEffect will update localAiHint when aiHint changes
    } catch (error) {
      setAiError('AI mentor unavailable. Try again.');
      setLocalAiHint({ text: 'AI mentor unavailable. Try again.', error: true });
    } finally {
      setAiLoading(false);
    }
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    try {
      const result = await testGeminiConnection();
      if (result.success) {
        alert(`✅ API Connection Successful!\nResponse: ${result.response}`);
      } else {
        alert(`❌ API Connection Failed:\n${result.error}`);
      }
    } catch (error) {
      alert(`❌ Test Failed:\n${error.message}`);
    } finally {
      setTestingConnection(false);
    }
  };

  return (
    <div className="bg-white border-t border-gray-200 p-4 relative">
      {/* Celebration Modal */}
      {showCelebration && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md mx-4 text-center shadow-2xl animate-bounce">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Challenge Completed!</h2>
            <p className="text-gray-600 mb-4">
              Excellent work! You've completed <strong>{completedChallenges.length}</strong> challenge{completedChallenges.length !== 1 ? 's' : ''} so far.
            </p>
            <button
              onClick={closeCelebration}
              className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-lg font-medium transition-colors"
            >
              Continue Learning
            </button>
          </div>
        </div>
      )}
      {currentProgress.isComplete && (
        <div className="mb-6 p-6 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl text-center shadow-lg">
          <div className="text-4xl mb-2">🎉</div>
          <h3 className="text-xl font-bold mb-2">Challenge Completed!</h3>
          <p className="text-sm opacity-90">Excellent work! You've successfully completed all objectives.</p>
        </div>
      )}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">{challenge.title}</h3>
          <p className="text-sm text-gray-600">{challenge.description}</p>
        </div>
        <div className="flex items-center space-x-2">
          <span className={`px-2 py-1 rounded text-xs font-medium ${
            challenge.difficulty === 'beginner' ? 'bg-green-100 text-green-800' :
            challenge.difficulty === 'intermediate' ? 'bg-yellow-100 text-yellow-800' :
            'bg-red-100 text-red-800'
          }`}>
            {challenge.difficulty}
          </span>
          {challenge.assistiveTechFocus && (
            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
              ♿ Assistive Tech
            </span>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between text-sm text-gray-600 mb-1">
          <span>Progress</span>
          <span>{currentProgress.completed}/{challenge.objectives.length} objectives</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
          <div 
            className={`h-3 rounded-full transition-all duration-700 ease-out ${
              currentProgress.isComplete ? 'bg-gradient-to-r from-green-400 to-green-600 shadow-lg' : 'bg-gradient-to-r from-blue-500 to-blue-600'
            } ${currentProgress.isComplete ? 'animate-pulse' : ''}`}
            style={{ width: `${progressPercentage}%` }}
          ></div>
        </div>
      </div>

      {/* Objectives */}
      <div className="mb-4">
        <h4 className="font-medium text-gray-800 mb-2">Objectives:</h4>
        <div className="space-y-2">
          {challenge.objectives.map((objective, index) => (
            <div key={index} className="flex items-start space-x-2">
              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                currentProgress.completedObjectives.includes(index)
                  ? 'bg-green-500 border-green-500'
                  : 'border-gray-300'
              }`}>
                {currentProgress.completedObjectives.includes(index) && (
                  <span className="text-white text-xs">✓</span>
                )}
              </div>
              <span className={`text-sm ${
                currentProgress.completedObjectives.includes(index)
                  ? 'text-green-700 line-through'
                  : 'text-gray-700'
              }`}>
                {objective}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Real-world Context */}
      {challenge.realWorldContext && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
          <h4 className="font-medium text-blue-800 mb-1">💡 Real-world Impact</h4>
          <p className="text-sm text-blue-700">{challenge.realWorldContext}</p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={onHint}
          className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 text-sm font-medium"
        >
          💡 Hint
        </button>
        <button
          onClick={handleAIHint}
          disabled={aiLoading || !isGeminiConfigured() || !user}
          title={
            !user
              ? 'Sign in with Google to use AI hints'
              : !isGeminiConfigured()
              ? 'Add Gemini API key to .env to enable AI hints'
              : ''
          }
          className={`px-4 py-2 text-white rounded text-sm font-medium ${
            aiLoading
              ? 'bg-purple-400 cursor-not-allowed'
              : !isGeminiConfigured() || !user
              ? 'bg-gray-400 cursor-not-allowed opacity-50'
              : 'bg-purple-500 hover:bg-purple-600'
          }`}
        >
          {aiLoading ? (
            <>
              <span className="inline-block animate-spin mr-1">🤖</span>
              Thinking...
            </>
          ) : !user ? (
            <>
              <span className="mr-1">🔒</span>
              Sign in for AI Hints
            </>
          ) : (
            <>
              <span className="mr-1">🤖</span>
              AI Hint
            </>
          )}
        </button>
        <button
          onClick={handleValidate}
          disabled={isChecking || currentProgress.isComplete}
          className={`px-4 py-2 text-white rounded text-sm font-medium ${
            currentProgress.isComplete
              ? 'bg-green-600 cursor-not-allowed'
              : isChecking 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-blue-500 hover:bg-blue-600'
          }`}
        >
          {currentProgress.isComplete ? (
            <>
              <span className="mr-1">✅</span>
              Challenge Completed
            </>
          ) : isChecking ? (
            <>
              <span className="inline-block animate-spin mr-1">⏳</span>
              Checking...
            </>
          ) : (
            '✓ Check Progress'
          )}
        </button>
        {user && isGeminiConfigured() && (
          <button
            onClick={handleTestConnection}
            disabled={testingConnection}
            className={`px-3 py-2 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-50 ${
              testingConnection ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            title="Test Gemini API connection"
          >
            {testingConnection ? '🔄 Testing...' : '🔧 Test API'}
          </button>
        )}
      </div>

      {/* AI Controls */}
      <div className="flex items-center justify-between mb-4">
        <label className="flex items-center space-x-2 text-sm">
          <input
            type="checkbox"
            checked={simpleMode}
            onChange={(e) => setSimpleMode(e.target.checked)}
            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
          />
          <span className="text-gray-700">Explain like I'm 12</span>
        </label>
        <div className="text-sm text-gray-500">
          ⏱️ Est. {challenge.estimatedTime}
        </div>
      </div>

      {/* Login encouragement for AI features */}
      {!user && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-700">
            <span className="font-medium">💡 Pro tip:</span> Sign in with Google to unlock AI-powered design hints that adapt to your progress!
          </p>
        </div>
      )}

      {/* API Setup Warning */}
      {user && !isGeminiConfigured() && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-700">
            <span className="font-medium">⚠️ Setup Required:</span> Add your Gemini API key to the .env file as VITE_GEMINI_API_KEY to enable AI hints.
          </p>
        </div>
      )}

      {/* Current Hint */}
      {currentProgress.currentHint && (
        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
          <p className="text-sm text-yellow-800">
            <strong>Hint:</strong> {currentProgress.currentHint}
          </p>
        </div>
      )}

      {/* Error Message */}
      {currentProgress.error && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
          <p className="text-sm text-red-800">
            <strong>Error:</strong> {currentProgress.error}
          </p>
        </div>
      )}

      {/* AI Hint Response - Highly Visible */}
      {(aiLoading || localAiHint) && (
        <div className={`mt-4 p-5 rounded-xl border-2 shadow-lg transition-all duration-500 ease-in-out transform ${
          aiLoading ? 'animate-pulse bg-gradient-to-r from-purple-50 to-blue-50 border-purple-300' :
          aiError || (localAiHint && localAiHint.error)
            ? 'bg-gradient-to-r from-red-50 to-pink-50 border-red-300 animate-in slide-in-from-top-2'
            : 'bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-300 animate-in slide-in-from-top-2'
        }`}>
          <div className="flex items-start space-x-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center shadow-md ${
              aiLoading ? 'bg-purple-100 animate-bounce' :
              aiError || (localAiHint && localAiHint.error) ? 'bg-red-100' : 'bg-purple-100'
            }`}>
              <span className="text-2xl">
                {aiLoading ? '🤖' : (aiError || (localAiHint && localAiHint.error) ? '❌' : '🤖')}
              </span>
            </div>
            <div className="flex-1">
              <div className="flex items-center mb-2">
                <h4 className={`font-bold text-lg ${
                  aiError || (localAiHint && localAiHint.error) ? 'text-red-800' : 'text-purple-800'
                }`}>
                  {aiLoading ? '🤖 AI Mentor Thinking...' : (aiError || (localAiHint && localAiHint.error) ? '❌ AI Hint Error' : '🤖 AI Design Mentor')}
                </h4>
                {localAiHint && localAiHint.simpleMode && (
                  <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                    Simple Mode
                  </span>
                )}
              </div>
              <p className={`text-base leading-relaxed ${
                aiError || (localAiHint && localAiHint.error) ? 'text-red-700' : 'text-purple-700'
              }`}>
                {aiLoading ? 'Generating personalized hint...' : (localAiHint ? localAiHint.text : aiError)}
              </p>
              {(aiError || (localAiHint && localAiHint.error)) && !aiLoading && (
                <div className="mt-3 flex justify-end">
                  <button
                    onClick={handleAIHint}
                    className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm rounded-md transition-colors flex items-center space-x-1"
                  >
                    <span>🔄</span>
                    <span>Retry</span>
                  </button>
                </div>
              )}
              {localAiHint && !aiLoading && !localAiHint.error && (
                <div className="mt-3 flex items-center text-xs text-gray-500">
                  <span className="mr-2">🕒</span>
                  Generated {new Date(localAiHint.timestamp).toLocaleTimeString()}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {showToast && (
        <Toast 
          message={toastMessage}
          type="success"
          duration={4000}
          onClose={() => setShowToast(false)}
        />
      )}

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default MissionPanel;
